import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, CreditCard, Building2, Mail, MonitorSmartphone, ShieldAlert,
  ArrowRight, Check, Lock,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import { PageHero } from "./Services";
import { brand, payments, heroImage } from "../data/site";

// Every method is gated on its own config value, so a blank field in
// src/data/site.js removes the card instead of rendering a dead button.
// Order is deliberate: the ways most people will actually pay come first.
const methods = [
  payments.onlineUrl && {
    icon: CreditCard,
    title: "Pay online",
    body: payments.processorName
      ? `Card or bank transfer through ${payments.processorName}, who processes our payments. The charge on your statement will show their name alongside ours.`
      : "Pay your invoice by card or bank transfer. The payment is handled by our payment processor, not by this website.",
    cta: { label: "Open the payment page", href: payments.onlineUrl, external: true },
  },
  payments.portalUrl && {
    icon: MonitorSmartphone,
    title: "Your customer portal",
    body: "Every job gets a private portal. Your invoice, scope, photos and warranty paperwork are in one place, and you can pay from there.",
    cta: { label: "Sign in to your portal", href: payments.portalUrl, external: true },
  },
  payments.acceptsPhonePayment && {
    icon: Phone,
    title: "Over the phone",
    body: "Call during business hours and we will take the payment with you on the line. Have your invoice number handy — it is at the top of the invoice we emailed you.",
    cta: { label: brand.phone, href: `tel:${brand.phoneRaw}` },
  },
  payments.zelle && {
    icon: Building2,
    title: "Bank transfer",
    body: `Send to ${payments.zelle}. Put your invoice number in the memo so it lands against the right job, and keep the confirmation.`,
  },
  payments.checkPayableTo && brand.address && {
    icon: Mail,
    title: "Check by mail",
    body: `Make it payable to ${payments.checkPayableTo} and mail it to ${brand.address}. Write your invoice number on the memo line.`,
  },
  payments.acceptsInPerson && {
    icon: Check,
    title: "In person",
    body: "You can hand a check or pay by card at the end of the job. Ask for a receipt before the crew leaves — you should never have to chase us for one.",
  },
].filter(Boolean);

const faqs = [
  {
    q: "When is payment due?",
    a: "Whatever your written contract says. Deposits, progress payments and the final balance are all set out in the contract before any work starts, and we do not invent a payment schedule afterwards.",
  },
  {
    q: "Do I pay before the work is done?",
    a: "A deposit may be due at signing, and the amount is in your contract. For roof repair or replacement after a storm, where the work is going through an insurance claim, Arizona law caps the down payment at signing at fifty percent of the contract price. The balance follows the schedule in your contract.",
  },
  {
    q: "Can you pay or waive my insurance deductible?",
    a: "No, and you should walk away from any contractor who offers to. Arizona law specifically prohibits a contractor from paying, promising to pay, advertising or rebating your insurance deductible. A contractor who offers it is telling you how they treat rules generally.",
  },
  {
    q: "Can I pay with my insurance check?",
    a: "Usually yes. Insurance claims are often paid in two parts, and your mortgage company may have to endorse the check as well. We will tell you exactly which payments we expect and when, before the work starts rather than after.",
  },
  {
    q: "What if I cannot pay the whole amount at once?",
    a: "Say so before the work is scheduled rather than after the invoice arrives. There are financing options, and sometimes the better answer is phasing the work or repairing now and replacing later.",
  },
  {
    q: "Will I get a receipt?",
    a: "Yes. Every payment gets a receipt, and the final invoice is marked paid and goes in your file along with the photos and the warranty paperwork. That paid invoice is what a future buyer or insurer will ask to see.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "Make a Payment",
      url: `${SITE_URL}/make-a-payment`,
      publisher: { "@id": `${SITE_URL}/#business` },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Make a Payment", item: `${SITE_URL}/make-a-payment` },
      ],
    },
  ],
};

const MakePayment = () => (
  <div>
    <SEO
      title="Make a Payment | Pepla Roofing"
      description="Pay your Pepla Roofing invoice. Payment methods, what your contract says about deposits and schedules, and how to make sure a payment request is really from us."
      jsonLd={jsonLd}
    />

    <PageHero
      eyebrow="Billing"
      title="Make a Payment"
      subtitle="Ways to settle your invoice, and what your contract says about when anything is due."
      image={heroImage}
      imageAlt="Pepla Roofing job in Phoenix, Arizona"
    />

    {/* Ways to pay */}
    <section className="py-24 bg-background">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Ways To Pay</h2>
        <div className="brand-line mb-5" />
        <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mb-10">
          Have your invoice number ready whichever way you pay — it is what matches the payment to
          your job. If you are not sure what you owe, call us before sending anything.
        </p>

        <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
          <div className="grid sm:grid-cols-2 gap-px">
            {methods.map((m) => (
              <div key={m.title} className="bg-card flex flex-col gap-4 p-7 sm:p-8">
                <span className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0">
                  <m.icon size={18} className="text-brand-dark" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{m.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{m.body}</p>
                </div>
                {m.cta && (
                  <a
                    href={m.cta.href}
                    {...(m.cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline self-start"
                  >
                    {m.cta.label} <ArrowRight size={14} aria-hidden="true" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Until a processor link exists this is how someone actually pays, so
            it says so plainly rather than leaving the page looking unfinished. */}
        {!payments.onlineUrl && !payments.portalUrl && (
          <div className="mt-8 flex items-start gap-4 rounded-2xl border border-ink/15 bg-secondary px-7 py-6">
            <Lock size={19} className="text-brand-dark shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-muted-foreground text-sm leading-relaxed">
              We do not take card numbers through this website, and we never will — a form on a
              roofing site is not where your card details belong. To pay by card, call us and we will
              run it on a secure terminal while you are on the phone, or ask us to send you a payment
              link by email.
            </p>
          </div>
        )}
      </div>
    </section>

    {/* Fraud. Construction payment fraud is common enough that this is not
        boilerplate — stolen invoices and swapped bank details are the single
        most expensive thing that can happen to a homeowner on a re-roof. */}
    <section className="py-24 section-dark text-white">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="flex gap-4 mb-6">
          <ShieldAlert size={24} className="text-brand shrink-0 mt-1" aria-hidden="true" />
          <div>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
              Make Sure It Is Really Us
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed">
              Contractor payment fraud is real and it is expensive. Someone who gets hold of an
              invoice can send a convincing follow-up email asking you to pay a different account.
              Four things that are always true of us:
            </p>
          </div>
        </div>

        <ul className="space-y-4 lg:pl-10">
          {[
            "We will never email or text you to say our bank details have changed. If you get that message, it is not from us — call the number on this website before you send anything.",
            "We will never ask for payment by gift card, cryptocurrency, or a wire to a personal account.",
            "We will never demand immediate payment to avoid some consequence, or pressure you to pay before the work in your contract is done.",
            "Our phone number is the one on this page. Do not use a number from an email asking you for money — look it up here instead.",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3 text-white/85">
              <span className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                <Check size={13} className="text-brand" aria-hidden="true" />
              </span>
              <span className="leading-relaxed">{t}</span>
            </li>
          ))}
        </ul>

        <p className="text-white/50 text-sm leading-relaxed mt-8 lg:pl-10">
          If anything about a payment request feels wrong, stop and call {brand.phone}. We would much
          rather answer a question than watch someone lose money using our name.
        </p>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-24 bg-secondary">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
          Billing Questions
        </h2>
        <div className="brand-line mb-9" />
        <dl className="divide-y divide-ink/10">
          {faqs.map((f) => (
            <div key={f.q} className="py-6">
              <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
              <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <a
            href={`tel:${brand.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            <Phone size={17} /> {brand.phone}
          </a>
          <Link
            to="/financing"
            className="inline-flex items-center justify-center gap-2 border border-ink/20 text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md hover:border-brand transition-colors"
          >
            Financing options <ArrowRight size={16} />
          </Link>
        </div>

        <p className="text-ink/45 text-xs leading-relaxed mt-10">
          Deposits, progress payments and final balances follow your written contract. For residential
          roof repair or replacement after a catastrophic storm in an area designated by your insurer,
          Arizona law (A.R.S. § 32-1158.02) caps the down payment at contract signing at fifty percent
          of the contract price, requires change orders to be in writing and signed, and prohibits a
          contractor from paying, promising to pay, advertising or rebating your insurance deductible.
          That statute also gives you the right to cancel within four business days of signing, and
          within seventy-two hours of your insurer denying the claim.
        </p>
      </div>
    </section>
  </div>
);

export default MakePayment;
